import React from "react";
import { Sparkles, Plus, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SYSTEMS_TEMPLATES = ["rpg3d", "explorer3d", "collector3d", "platformer3d", "shooter3d", "fps3d", "battle3d", "runner3d", "maze3d", "horror3d"];
const STATS = [["maxHp", "❤️ Max health"], ["speed", "⚡ Move speed"], ["jump", "🦘 Jump"], ["damage", "⚔️ Attack"], ["coins", "🪙 Coin bonus"]];

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 24);

const STARTER = {
  earnEvery: 30,
  nodes: [
    { id: "vitality", name: "Vitality", icon: "❤️", cost: 1, effect: { stat: "maxHp", value: 1 }, requires: [] },
    { id: "swift", name: "Swiftness", icon: "⚡", cost: 1, effect: { stat: "speed", value: 0.2 }, requires: [] },
    { id: "might", name: "Might", icon: "⚔️", cost: 2, effect: { stat: "damage", value: 0.5 }, requires: ["vitality"] },
    { id: "fortune", name: "Fortune", icon: "🪙", cost: 2, effect: { stat: "coins", value: 0.5 }, requires: [] },
  ],
};

// Hand-editable skill tree (RPG progression). Writes config.skills.
export function SkillTreePanel({ config, setField }) {
  const tpl = config?.template || "";
  const skills = config?.skills && Array.isArray(config.skills.nodes) ? config.skills : null;
  const nodes = skills?.nodes || [];

  const show = SYSTEMS_TEMPLATES.includes(tpl) || !!skills;
  if (!show) return null;

  const write = (s) => setField("skills", s);
  const seed = () => write({ earnEvery: STARTER.earnEvery, nodes: STARTER.nodes.map((n) => ({ ...n, effect: { ...n.effect }, requires: [...n.requires] })) });
  const setNodes = (arr) => write({ earnEvery: skills?.earnEvery || 30, nodes: arr });
  const updateNode = (i, patch) => setNodes(nodes.map((n, k) => (k === i ? { ...n, ...patch } : n)));
  const addNode = () => setNodes([...nodes, { id: "skill" + (nodes.length + 1), name: "New Skill", icon: "⭐", cost: 1, effect: { stat: "maxHp", value: 1 }, requires: [] }]);
  const removeNode = (i) => setNodes(nodes.filter((_, k) => k !== i));

  return (
    <div className="pt-2 border-t border-[hsl(var(--border))] space-y-3" data-testid="skill-tree-panel">
      <div className="flex items-center gap-1.5">
        <div className="text-xs font-bold flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Skill Tree</div>
        {skills && (
          <div className="ml-auto flex items-center gap-1.5 text-[10px] text-[hsl(var(--muted-foreground))]">
            <span>Points every</span>
            <Input type="number" min={5} max={200} value={skills.earnEvery || 30} onChange={(e) => write({ ...skills, earnEvery: Math.max(5, +e.target.value || 30) })} className="h-6 w-14 text-center px-1" data-testid="skill-earnevery" />
            <span>score</span>
          </div>
        )}
      </div>

      {!skills ? (
        <button type="button" onClick={seed} data-testid="add-skill-tree-btn"
          className="w-full text-[11px] font-semibold px-2 py-2 rounded-lg border border-dashed border-[hsl(var(--primary))]/50 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 flex items-center justify-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Add a skill tree
        </button>
      ) : (
        <div className="space-y-1.5">
          {nodes.map((n, i) => {
            const eff = n.effect || { stat: "maxHp", value: 1 };
            const isMax = eff.stat === "maxHp";
            const otherIds = nodes.map((x) => x.id).filter((id) => id && id !== n.id);
            return (
              <div key={i} className="rounded-lg border border-[hsl(var(--border))] p-2 space-y-1.5" data-testid={`skill-row-${i}`}>
                <div className="flex items-center gap-1.5">
                  <Input value={n.icon || "⭐"} onChange={(e) => updateNode(i, { icon: e.target.value.slice(0, 4) })} className="h-8 w-11 text-center px-1" data-testid={`skill-icon-${i}`} />
                  <Input value={n.name || ""} onChange={(e) => updateNode(i, { name: e.target.value, id: n.id || slug(e.target.value) })} placeholder="Skill name" className="h-8 flex-1 min-w-0" data-testid={`skill-name-${i}`} />
                  <button type="button" onClick={() => removeNode(i)} className="h-8 w-8 grid place-items-center rounded-lg border border-[hsl(var(--border))] hover:border-rose-500/60 text-[hsl(var(--muted-foreground))] hover:text-rose-500" data-testid={`skill-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex items-center gap-1.5">
                  <Select value={eff.stat} onValueChange={(v) => updateNode(i, { effect: { stat: v, value: v === "maxHp" ? 1 : 0.2 } })}>
                    <SelectTrigger className="h-7 flex-1 text-xs" data-testid={`skill-stat-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step={isMax ? 1 : 0.1} min={isMax ? 1 : 0.05} max={isMax ? 5 : 2} value={eff.value} onChange={(e) => updateNode(i, { effect: { ...eff, value: +e.target.value || (isMax ? 1 : 0.2) } })} title={isMax ? "hearts" : "fraction (0.2 = +20%)"} className="h-7 w-16 text-center" data-testid={`skill-value-${i}`} />
                  <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{isMax ? "❤️" : "×"}</span>
                  <span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-1">cost</span>
                  <Input type="number" min={1} max={10} value={n.cost || 1} onChange={(e) => updateNode(i, { cost: Math.max(1, +e.target.value || 1) })} className="h-7 w-14 text-center" data-testid={`skill-cost-${i}`} />
                </div>
                {otherIds.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-[hsl(var(--muted-foreground))]">Requires:</span>
                    <Select value={(n.requires && n.requires[0]) || "__none__"} onValueChange={(v) => updateNode(i, { requires: v === "__none__" ? [] : [v] })}>
                      <SelectTrigger className="h-7 flex-1 text-xs" data-testid={`skill-requires-${i}`}><SelectValue placeholder="nothing" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">nothing</SelectItem>
                        {otherIds.map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={addNode} className="w-full h-8 text-xs" data-testid="add-skill-btn"><Plus className="w-3.5 h-3.5" /> Add skill</Button>
        </div>
      )}
    </div>
  );
}
